"use client";

import { ReactNode, createContext, useContext, useMemo } from "react";

import { Category } from "@/types/category";
import { ServiceDisplayMode } from "@/types/enums/service-display-mode";
import { PublicLocationDetail } from "@/types/location";
import { PublicProviderSummary } from "@/types/provider";
import { PublicServiceDetail } from "@/types/service";

import { LocationType, PatientType } from "@/stores/self-schedule-store";

/**
 * Generic interface for service-step state. Any provider can implement this
 * contract — the real self-schedule flow, an admin preview, a Storybook harness.
 * Consumers depend on this interface only, not on any specific state source.
 */

export interface ServicesData {
  services: PublicServiceDetail[] | undefined;
  categories: Category[] | undefined;
  locations: PublicLocationDetail[] | undefined;
  providers: PublicProviderSummary[] | undefined;
  shouldGroupServicesByCategory: boolean | undefined;
  shouldGroupServicesByLocation: boolean | undefined;
  serviceDisplayMode: ServiceDisplayMode | undefined;
  showServiceDuration: boolean | undefined;
}

export interface ServicesState {
  // Filter inputs
  searchQuery: string;
  selectedCategory: string | undefined;
  locationType: LocationType | null;
  patientType: PatientType | null;
  selectedLocationId: string | null;
  userLocation: { lat: number; lng: number; accuracy?: number } | null;

  // Selection
  selectedServiceIds: string[];
  selectedServices: PublicServiceDetail[];
  effectiveNumAllowedServices: number;
  /** Null when no specific provider is committed (including "first available"). */
  selectedProviderId: string | null;

  // Derived
  selectedLocation: PublicLocationDetail | undefined;

  // Data
  data: ServicesData | undefined;
  isLoading: boolean;
}

export interface ServicesActions {
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (category: string | undefined) => void;
  /**
   * Append a new instance of the given service. Returns the new instance id
   * (or void when the host doesn't support selection — e.g. admin preview).
   */
  addService: (serviceId: string, serviceName?: string) => string | void;
  /** Remove one selected instance of the given service, if present. */
  removeService: (serviceId: string) => void;
  /** Remove every selected instance of the given service. */
  removeAllServiceInstances: (serviceId: string) => void;
  isServiceSelected: (serviceId: string) => boolean;
  /** Count of instances of a service currently in the cart. */
  getServiceInstanceCount: (serviceId: string) => number;
  requestUserLocation: () => void | Promise<void>;
  setSelectedLocationId: (id: string | null, name?: string) => void;
}

export interface ServicesContextValue {
  state: ServicesState;
  actions: ServicesActions;
}

const ServicesContext = createContext<ServicesContextValue | null>(null);

export function ServicesProvider({
  value,
  children,
}: {
  value: ServicesContextValue;
  children: ReactNode;
}) {
  return (
    <ServicesContext.Provider value={value}>
      {children}
    </ServicesContext.Provider>
  );
}

export function useServices(): ServicesContextValue {
  const ctx = useContext(ServicesContext);
  if (!ctx) {
    throw new Error("useServices must be used within a ServicesProvider");
  }
  return ctx;
}

/**
 * Assemble a ServicesContextValue from individual pieces. Keeps the interface
 * stable across providers by centralizing the object shape.
 */
export function useServicesContextValue(
  state: ServicesState,
  actions: ServicesActions,
): ServicesContextValue {
  return useMemo(() => ({ state, actions }), [state, actions]);
}
